import React from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/i18n/formatNumber";
import { filesize } from "src/ui/utils/filesize";
import { isUnlimited } from "src/ui/utils/limit";
import { makeStyles } from "tss-react/mui";

import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { featureOn, LimitFeatureTile } from "@dashboard/shared/limits/FeatureTile";
import { FeatureTilesGrid } from "@dashboard/shared/limits/FeatureTilesGrid";
import { getBarValue, getFeature } from "@dashboard/shared/limits/utils";
import { SectionHeader } from "@dashboard/shared/styles";
import { isLimitReached } from "@dashboard/workspaces/WorkspacesPage.utils";
import { Box, Theme, Typography, useTheme } from "@mui/material";
import { ProgressBar } from "@shared/ProgressBar";

type Props = {
    features: API.Feature[];
    editable?: boolean;
    onLimitClick?: (limitType: string, currentLimit: number) => void;
    variant?: "section" | "pane" | "bare";
};

const useLimitUsageStyles = makeStyles({ name: "SubscriptionLimitsBare" })((theme: Theme) => ({
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        columnGap: theme.spacing(5),
        rowGap: theme.spacing(2.5),
        width: "100%",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0, 1fr)",
        },
    },
    row: {
        display: "flex",
        flexDirection: "column",
        gap: 0,
        minWidth: 0,
    },
    header: {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        minWidth: 0,
    },
    label: {
        minWidth: 0,
        color: theme.palette.text.secondary,
    },
    usage: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: theme.spacing(1),
        flexShrink: 0,
        whiteSpace: "nowrap",
        color: theme.palette.text.primary,
        fontVariantNumeric: "tabular-nums",
    },
    warningIcon: {
        color: theme.palette.emteria.warning,
    },
    progress: {
        width: "100%",
        minWidth: 0,
        "& [data-testid='progress-bar'] > div:first-of-type": {
            height: 6,
        },
    },
}));

type LimitUsageType =
    | "LicenseLimit"
    | "UserLimit"
    | "GroupLimit"
    | "GroupNestingLimit"
    | "ProductLimit"
    | "BuildLimit"
    | "FileCountLimit"
    | "FileSizeLimit"
    | "PolicyLimit"
    | "CommandLimit"
    | "ProvisioningLimit"
    | "DeviceStateHistoryLimit"
    | "FDroidRepoLimit"
    | "StreamSessionLimit";

type LimitUsageRowProps = {
    limitType: LimitUsageType;
    features: API.Feature[];
};

function LimitUsageRow(props: LimitUsageRowProps) {
    const { t, i18n } = useTranslation(["common"]);
    const { classes } = useLimitUsageStyles();
    const theme = useTheme();

    const { used = 0, max } = getFeature(props.limitType, props.features);
    const unlimited = isUnlimited(max, props.limitType);
    const reached = isLimitReached(used, max) && !unlimited;
    const formattedUsed = props.limitType === "FileSizeLimit" ? filesize(used) : formatNumber(used, i18n.language);
    const usageLabel = unlimited
        ? `${formattedUsed} / ${t("common:label.unlimited")}`
        : `${formattedUsed} / ${formatNumber(max, i18n.language)}`;
    const rawProgress = unlimited ? (used > 0 ? 5 : 0) : getBarValue(used, max);
    const progress = Math.min(100, rawProgress);
    const needsAttention = !unlimited && (reached || progress > 80);
    const color = needsAttention ? theme.palette.emteria.warning : theme.palette.emteria.primary;

    return (
        <Box className={classes.row}>
            <Box className={classes.header}>
                <Typography variant="body1" className={classes.label}>
                    {t(`common:limits.${props.limitType}`)}
                </Typography>
                <Typography
                    variant="body1"
                    component="span"
                    className={classes.usage}
                    data-testid={`limitFeatureTile.${props.limitType}`}
                >
                    {usageLabel}
                </Typography>
            </Box>
            <Box className={classes.progress}>
                <ProgressBar progress={progress} color={color} hideLabel />
            </Box>
        </Box>
    );
}

function LimitUsageList(props: { features: API.Feature[] }) {
    const { classes } = useLimitUsageStyles();
    const deviceManagement = getFeature("DeviceManagement", props.features);
    const showDeviceManagementLimits = deviceManagement.used === featureOn;

    return (
        <Box className={classes.grid}>
            <LimitUsageRow limitType="LicenseLimit" features={props.features} />
            <LimitUsageRow limitType="UserLimit" features={props.features} />
            <LimitUsageRow limitType="GroupLimit" features={props.features} />
            <LimitUsageRow limitType="ProductLimit" features={props.features} />
            <LimitUsageRow limitType="BuildLimit" features={props.features} />
            <LimitUsageRow limitType="FileCountLimit" features={props.features} />
            <LimitUsageRow limitType="PolicyLimit" features={props.features} />
            {showDeviceManagementLimits ? <LimitUsageRow limitType="CommandLimit" features={props.features} /> : null}
            <LimitUsageRow limitType="ProvisioningLimit" features={props.features} />
            {showDeviceManagementLimits ? <LimitUsageRow limitType="DeviceStateHistoryLimit" features={props.features} /> : null}
            <LimitUsageRow limitType="FDroidRepoLimit" features={props.features} />
            <LimitUsageRow limitType="StreamSessionLimit" features={props.features} />
        </Box>
    );
}

type LimitFeatureTilesProps = {
    features: API.Feature[];
    editable: boolean;
    onLimitClick?: (limitType: string, currentLimit: number) => void;
};

function LimitFeatureTiles(props: LimitFeatureTilesProps) {
    const { t } = useTranslation(["common"]);
    const { features, editable, onLimitClick } = props;
    const deviceManagement = getFeature("DeviceManagement", features);
    const showDeviceManagementLimits = deviceManagement.used === featureOn;

    return (
        <FeatureTilesGrid>
            <LimitFeatureTile
                limitType="BuildLimit"
                title={t("common:limits.BuildLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />

            {showDeviceManagementLimits ? (
                <React.Fragment>
                    <LimitFeatureTile
                        limitType="CommandLimit"
                        title={t("common:limits.CommandLimit")}
                        features={features}
                        editable={false}
                        onClick={onLimitClick}
                    />
                    <LimitFeatureTile
                        limitType="DeviceStateHistoryLimit"
                        title={t("common:limits.DeviceStateHistoryLimit")}
                        features={features}
                        editable={false}
                        onClick={onLimitClick}
                    />
                </React.Fragment>
            ) : null}
            <LimitFeatureTile
                limitType="FDroidRepoLimit"
                title={t("common:limits.FDroidRepoLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="FileCountLimit"
                title={t("common:limits.FileCountLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                title={t("common:limits.FileSizeLimit")}
                limitType="FileSizeLimit"
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="GroupLimit"
                title={t("common:limits.GroupLimit")}
                features={features}
                editable={false}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="GroupNestingLimit"
                title={t("common:limits.GroupNestingLimit")}
                features={features}
                editable={false}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="LicenseLimit"
                title={t("common:limits.LicenseLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="PolicyLimit"
                title={t("common:limits.PolicyLimit")}
                features={features}
                editable={false}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="ProductLimit"
                title={t("common:limits.ProductLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="ProvisioningLimit"
                title={t("common:limits.ProvisioningLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="StreamSessionLimit"
                title={t("common:limits.StreamSessionLimit")}
                features={features}
                editable={false}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="UserLimit"
                title={t("common:limits.UserLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
        </FeatureTilesGrid>
    );
}

export const SubscriptionLimits = (props: Props) => {
    const { t } = useTranslation(["pages", "common"]);
    const { features, editable = false, onLimitClick, variant = "section" } = props;
    const header = t("common:subscription.limits");

    if (variant === "bare") {
        return <LimitUsageList features={features} />;
    }

    if (variant === "pane") {
        return (
            <>
                <PaneElement.Header>{header}</PaneElement.Header>
                <LimitFeatureTiles features={features} editable={editable} onLimitClick={onLimitClick} />
            </>
        );
    }

    return (
        <>
            <SectionHeader>{header}</SectionHeader>
            <LimitFeatureTiles features={features} editable={editable} onLimitClick={onLimitClick} />
        </>
    );
};
